import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Hourglass, Send, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API = `${window.__BACKEND_URL__}/api`;
const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

// Dashboard tile add-on: estimates awaiting a reply > N days, one-click follow-up email.
export const EstimateFollowUps = ({ token, days = 7 }) => {
  const { toast } = useToast();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState('');
  const load = () => axios.get(`${API}/estimates/follow-ups?days=${days}`, auth).then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { if (token) load(); }, [token, days]);
  if (!data || data.count === 0) return null;
  const remind = async (it) => {
    setSending(it.id);
    try {
      const r = await axios.post(`${API}/estimates/${it.id}/remind`, {}, auth);
      toast({ title: `Follow-up sent for ${it.number}`, description: `Emailed ${r.data.to}` });
      await load();
    } catch (e) { toast({ title: 'Could not send', description: e.response?.data?.detail || e.message, variant: 'destructive' }); }
    finally { setSending(''); }
  };
  return (
    <div className="mb-4 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3" data-testid="estimate-followups">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 text-left text-sm" data-testid="estimate-followups-toggle">
        <Hourglass className="w-4 h-4 text-amber-300 shrink-0" />
        <span className="text-amber-100"><b data-testid="estimate-followups-count">{data.count}</b> estimate{data.count === 1 ? '' : 's'} awaiting a reply for more than {days} days</span>
        {open ? <ChevronUp className="w-4 h-4 ml-auto text-amber-300" /> : <ChevronDown className="w-4 h-4 ml-auto text-amber-300" />}
      </button>
      {open && (
        <ul className="mt-2 space-y-1 max-h-48 overflow-auto" data-testid="estimate-followups-list">
          {data.items.map((it) => (
            <li key={it.id} className="flex flex-wrap items-center gap-2 text-xs rounded-lg px-2 py-1.5" style={{ background: '#0F1923', border: '1px solid #2D3748' }} data-testid={`estimate-followup-${it.id}`}>
              <span className="font-mono text-white">{it.number}</span>
              <span className="text-slate-300 truncate max-w-[10rem]">{it.customer}</span>
              <span className="text-slate-400">{fmtMoney(it.total)}</span>
              <span className="text-amber-300">{it.days_waiting}d · {it.status}</span>
              {it.reminders_sent > 0 && <span className="text-slate-500">reminded ×{it.reminders_sent} {String(it.last_reminder_at || '').slice(0, 10)}</span>}
              <button type="button" onClick={() => remind(it)} disabled={sending === it.id || !it.email} title={it.email ? `Email ${it.email}` : 'No customer email on this estimate'} className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold disabled:opacity-40" style={{ background: '#1E88E5', color: 'white' }} data-testid={`estimate-followup-remind-${it.id}`}>
                {sending === it.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Send reminder
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
